import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { NavigateOptions } from 'react-router';
import { useNavigate, useLocation } from 'react-router-dom';

import { LocationSearchParams } from '~/common/types/LocationSearchParams';

const parseOptions = { parseNumbers: true, parseBooleans: true };

interface SetVarsOptions extends NavigateOptions {
  pathname?: string;
}

export const useLocationSearchParams = <TVariables extends LocationSearchParams>(): [
  TVariables,
  (vars: TVariables | ((prev: TVariables) => TVariables), options?: SetVarsOptions) => void,
] => {
  const navigate = useNavigate();
  const location = useLocation();
  const varsRef = useRef<TVariables>({} as any);

  const vars = useMemo(() => {
    return queryString.parse(location.search, parseOptions) as any;
  }, [location.search]);

  const setVars = useCallback(
    (varsOrSetter: TVariables | ((prev: TVariables) => TVariables), { pathname, ...options }: SetVarsOptions = {}) => {
      const search: any = typeof varsOrSetter === 'function' ? varsOrSetter(varsRef.current) : varsRef.current;
      navigate({ pathname, search: queryString.stringify(search) }, options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    varsRef.current = vars;
  }, [vars]);

  return [vars, setVars];
};
